import { useEffect, useId, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import styles from './EnterpriseSearch.module.scss'

import { EnterpriseListItem } from '@/api/generated'
import { searchEnterprises } from '@/api/internal'
import Input from '@/components/Input'
import { Spinner } from '@/components/Spinner'

const DEBOUNCE_MS = 300
const MIN_NAME_LENGTH = 2
// An 8-char prefix is the short form we paste around in Slack and logs.
const MIN_ID_LENGTH = 8
const ID_PATTERN = /^[0-9a-f-]+$/

type SearchMode = 'name' | 'id'

interface EnterpriseSearchProps {
  onSelect: (enterprise: EnterpriseListItem) => void
  onError: (error: string | null) => void
}

const normalizeId = (value: string) => value.trim().toLowerCase()

const EnterpriseSearch = ({ onSelect, onError }: EnterpriseSearchProps) => {
  const [nameQuery, setNameQuery] = useState('')
  const [idQuery, setIdQuery] = useState('')
  const [mode, setMode] = useState<SearchMode>('name')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [dismissed, setDismissed] = useState(false)
  const searchContainerRef = useRef<HTMLDivElement>(null)
  const fieldId = useId()

  const activeQuery = mode === 'name' ? nameQuery.trim() : normalizeId(idQuery)
  const idIsInvalid = mode === 'id' && activeQuery.length > 0 && !ID_PATTERN.test(activeQuery)
  const minLength = mode === 'name' ? MIN_NAME_LENGTH : MIN_ID_LENGTH
  const isSearchable = activeQuery.length >= minLength && !idIsInvalid

  useEffect(() => {
    if (!isSearchable) {
      setDebouncedQuery('')
      return
    }

    const timeoutId = setTimeout(() => {
      setDebouncedQuery(activeQuery)
      setDismissed(false)
    }, DEBOUNCE_MS)

    return () => clearTimeout(timeoutId)
  }, [activeQuery, isSearchable])

  const { data, isFetching, isSuccess, error } = useQuery({
    queryKey: ['enterpriseSearch', debouncedQuery],
    queryFn: async () => {
      const response = await searchEnterprises(debouncedQuery)
      return response.data
    },
    enabled: debouncedQuery.length > 0,
  })

  // The shared endpoint also matches names and domains, so keep only real ID matches in ID mode.
  const searchResults =
    mode === 'id'
      ? (data?.enterprises ?? []).filter((enterprise) => enterprise.id.startsWith(debouncedQuery))
      : (data?.enterprises ?? [])
  const hiddenCount = mode === 'name' && data ? data.total - data.enterprises.length : 0

  useEffect(() => {
    if (error) {
      onError('Failed to search enterprises. Please try again.')
    }
  }, [error, onError])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setDismissed(true)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleNameInput = (value: string) => {
    setNameQuery(value)
    setIdQuery('')
    setMode('name')
  }

  const handleIdInput = (value: string) => {
    setIdQuery(value)
    setNameQuery('')
    setMode('id')
  }

  const handleSelect = (enterprise: EnterpriseListItem) => {
    setNameQuery('')
    setIdQuery('')
    setDebouncedQuery('')
    onSelect(enterprise)
  }

  const showResults = isSuccess && !dismissed && debouncedQuery.length > 0 && debouncedQuery === activeQuery

  const results = (
    <>
      {showResults && searchResults.length > 0 && (
        <div className={styles.searchResults} role="listbox">
          {searchResults.map((enterprise) => (
            <button
              key={enterprise.id}
              role="option"
              className={styles.searchResultItem}
              onClick={() => handleSelect(enterprise)}
            >
              <span className={styles.resultName}>{enterprise.name}</span>
              <span className={styles.resultMeta}>
                {enterprise.member_count} {enterprise.member_count === 1 ? 'member' : 'members'}
              </span>
            </button>
          ))}
          {hiddenCount > 0 && (
            <div className={styles.moreResults}>
              {hiddenCount} more {hiddenCount === 1 ? 'match' : 'matches'}. Add more words to narrow the results.
            </div>
          )}
        </div>
      )}
      {showResults && searchResults.length === 0 && !isFetching && (
        <div className={styles.searchResults}>
          <div className={styles.noResults}>No enterprises found</div>
        </div>
      )}
    </>
  )

  return (
    <div ref={searchContainerRef} className={styles.searchContainer}>
      <div className={styles.field}>
        <label className={styles.label} htmlFor={`${fieldId}-name`}>
          Search by name or domain
        </label>
        <div className={styles.searchInputWrapper}>
          <Input
            id={`${fieldId}-name`}
            type="text"
            className={styles.searchInput}
            placeholder="e.g. Hg Capital or hgcapital.com"
            value={nameQuery}
            onInput={handleNameInput}
          />
          {isFetching && mode === 'name' && (
            <div className={styles.searchSpinner}>
              <Spinner size={16} color="gray" />
            </div>
          )}
        </div>
        {mode === 'name' && results}
      </div>
      <div className={styles.field}>
        <label className={styles.label} htmlFor={`${fieldId}-id`}>
          Search by enterprise ID
        </label>
        <div className={styles.searchInputWrapper}>
          <Input
            id={`${fieldId}-id`}
            type="text"
            className={styles.searchInput}
            placeholder="Full ID or first 8 characters"
            value={idQuery}
            onInput={handleIdInput}
            aria-invalid={idIsInvalid}
            aria-describedby={idIsInvalid ? `${fieldId}-id-hint` : undefined}
          />
          {isFetching && mode === 'id' && (
            <div className={styles.searchSpinner}>
              <Spinner size={16} color="gray" />
            </div>
          )}
        </div>
        {idIsInvalid && (
          <span id={`${fieldId}-id-hint`} className={styles.hint}>
            Enterprise IDs only contain 0-9, a-f and dashes
          </span>
        )}
        {mode === 'id' && results}
      </div>
    </div>
  )
}

export default EnterpriseSearch
